import { Flex, TextV2 } from '@withjoy/joykit';
import { Car, MapMarker } from '@withjoy/joykit/icons';
import React from 'react';
import { WalkIcon } from '@assets/icons';
import { AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { distanceInMiles, getWalkingAndDrivingDistance } from '@shared/utils/distanceInMiles';
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { WALKING_TIME_THRESHOLD_MINUTES } from '@apps/accommodations/constants';
import { useFormatTravelDistance } from '@shared/utils/hooks/useFormatTravelDistance';
import { Maybe } from '@graphql/generated';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';

interface TravelLogisticsProps
  extends Readonly<{
    useShortWording?: boolean;
    venue: { name: string; location: Maybe<LatLngLiteral> };
    listing: AccommodationListingDTO;
  }> {}

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'row', md: 'column' }}
      flexWrap={'wrap'}
      alignItems={{ _: 'center', md: 'start' }}
      columnGap={4}
      rowGap={1}
    >
      {children}
    </Flex>
  );
};

const Row = ({ icon, children }: { children: React.ReactNode; icon: JSX.Element }) => {
  return (
    <TextV2
      typographyVariant="label2"
      display="inline-flex"
      alignItems="center"
      columnGap={'2px'}
    >
      {icon} {children}
    </TextV2>
  );
};

const TravelLogistics = (props: TravelLogisticsProps) => {
  const { tq } = useTranslation('accommodationsConsole');

  const formatTravelDistance = useFormatTravelDistance();

  const { venue, listing, useShortWording } = props;

  const distanceFromTheVenue = distanceInMiles(venue?.location, listing.location);

  // Return null if distance is missing or exactly zero
  if (!distanceFromTheVenue) return null;

  const travelTimes = getWalkingAndDrivingDistance(distanceFromTheVenue);

  // Get display travel distance in miles/km
  const { formatted: formattedDistance, unitLabel: distanceUnitLabel } = formatTravelDistance({ value: distanceFromTheVenue, type: 'distance' });
  const displayDistanceInMiles = useShortWording ? tq`${formattedDistance} ${distanceUnitLabel}` : tq`${formattedDistance} ${distanceUnitLabel} from venue`;

  // Get display travel walking distance in minutes
  const { formatted: formattedWalkingDistance, unitLabel: walkingDistanceUnitLabel } = formatTravelDistance({ value: travelTimes.walking, type: 'time' });
  const displayWalkingDistance = useShortWording ? tq`${formattedWalkingDistance} ${walkingDistanceUnitLabel}` : tq`${formattedWalkingDistance} ${walkingDistanceUnitLabel} walk`;

  // Get display travel driving distance in minutes
  const { formatted: formattedDriveDistance, unitLabel: driveDistanceUnitLabel } = formatTravelDistance({ value: travelTimes.driving, type: 'time' });
  const displayDriveDistance = useShortWording ? tq`${formattedDriveDistance} ${driveDistanceUnitLabel}` : tq`${formattedDriveDistance} ${driveDistanceUnitLabel} drive`;

  return (
    <Container>
      <Row icon={<MapMarker size={16} />}>{displayDistanceInMiles}</Row>
      {travelTimes.walking < WALKING_TIME_THRESHOLD_MINUTES ? <Row icon={<WalkIcon />}>{displayWalkingDistance}</Row> : <Row icon={<Car size={16} />}>{displayDriveDistance}</Row>}
    </Container>
  );
};

TravelLogistics.Skeleton = () => {
  return (
    <Container>
      <Skeleton
        width={130}
        height={18}
      />
      <Skeleton
        width={75}
        height={18}
      />
      <Skeleton
        width={80}
        height={18}
      />
    </Container>
  );
};

export { TravelLogistics };
